"use strict";

require("core-js/modules/es.object.assign.js");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.GeoLocationContext = void 0;
exports.GeoLocationProvider = GeoLocationProvider;
require("core-js/modules/es.promise.js");
require("core-js/modules/web.dom-collections.iterator.js");
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _axios = _interopRequireDefault(require("axios"));
var _getAPIBase = require("../utils/helpers/magnolia/getAPIBase");
var _jsxFileName = "/Users/anthony.hessler/Workspace/Interactive/web-tools-io/src/lib/context/GeoLocationContext.js";
const _excluded = ["children"];
/**
 * @module GeoLocationContext
 */
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
/**
 * @typedef {object} GeoLocationContext
 * @property {Function} fetchData - Function to retrieve necessary data for geolocation service and functionality.
 * @property {LcGeolocationData} geolocationData - Geolocation data object from Life.Church geolocation service.
 * @property {Array} lifeChurchLocations - Array of Life.Church location data objects.
 */

/**
 * @typedef {object} LcGeolocationData
 * @property {string} city - The city value (e.g. 'edmond').
 * @property {boolean} cpa - Boolean flag denoting a CPA (Colorado Privacy Act) location.
 * @property {string} country_code - The two-letter country code (e.g. 'US').
 * @property {string} country_name - The country name (e.g. 'united states').
 * @property {boolean} gdpr - Boolean flag denoting a GDPR (General Data Protection Regulation) location.
 * @property {string} ip - The IP address value (e.g. '68.55.100.123').
 * @property {string} latitude - The latitude value (e.g. '35.649679').
 * @property {string} longitude - The longitude value (e.g. '-97.422060').
 * @property {string} region - The region/state/territory value (e.g. 'OK').
 */

const GeoLocationContext = exports.GeoLocationContext = /*#__PURE__*/_react.default.createContext({
  fetchData: null,
  geolocationData: null,
  lifeChurchLocations: null
});
GeoLocationContext.displayName = 'GeoLocationContext';

/**
 * React Context Provider for Life.Church geo-location service.
 *
 * @param {object} props - The context component props object.
 * @param {React.ReactNode} props.children - The React children around which the context provider is wrapped.
 *
 * @returns {React.ReactElement} The Geo Location Context Provider.
 */
function GeoLocationProvider(_ref) {
  let {
      children
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  /**
   * Data object to store geolocation data as retrieved from the Life.Church geolocation service.
   */
  const [geolocationData, setGeolocationData] = _react.default.useState({
    city: '',
    country_code: '',
    country_name: '',
    cpa: false,
    gdpr: false,
    ip: '',
    latitude: '',
    longitude: '',
    region: ''
  });

  /**
   * Array of Life.Church locations retrieved from the Magnolia CMS when mgnlEnvVars are provided and fetchLocations is true.
   */
  const [lifeChurchLocations, setLifeChurchLocations] = _react.default.useState(null);

  // Convenience state for initial data retrieval check.
  const [isInitialDataFetched, setIsInitialDataFetched] = _react.default.useState(false);

  /**
   * Life.Church Geolocation service URL.
   */
  const geoUrl = 'https://geo.life.church/where';

  /**
   * Function to retrieve necessary data for user geolocation data.
   *
   * @param {object} params - The function params object.
   * @param {Function} [params.callback] - Optional callback function triggered after data retrieval complete.
   *
   * @returns {object} Data object containing geolocationData and/or error data.
   */
  const fetchData = _react.default.useCallback(async function () {
    let {
      callback
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    try {
      var _ref2;
      const data = await _axios.default.get(geoUrl);
      setGeolocationData((_ref2 = data && data.data) !== null && _ref2 !== void 0 ? _ref2 : null);
      if (props.mgnlEnvVars && props.fetchLocations) {
        const locationsData = await _axios.default.get(encodeURI("".concat((0, _getAPIBase.getAPIBase)(props.mgnlEnvVars), "/.rest/delivery/location")));
        if (locationsData && locationsData.data && locationsData.data.results) {
          setLifeChurchLocations(locationsData.data.results);
        }
      }
      if (callback) {
        var _ref3;
        callback({
          geolocationData: (_ref3 = data && data.data) !== null && _ref3 !== void 0 ? _ref3 : null
        });
      }
    } catch (error) {
      if (callback) {
        callback({
          error
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Single-run convenience effect to fetch data and set up.
   */
  _react.default.useEffect(() => {
    if (!isInitialDataFetched) {
      setIsInitialDataFetched(true);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialDataFetched]);
  const value = _react.default.useMemo(() => ({
    fetchData,
    geolocationData,
    lifeChurchLocations
  }), [fetchData, geolocationData, lifeChurchLocations]);
  return /*#__PURE__*/_react.default.createElement(GeoLocationContext.Provider, _extends({
    value: value
  }, props, {
    __source: {
      fileName: _jsxFileName,
      lineNumber: 125,
      columnNumber: 5
    }
  }), children);
}
GeoLocationProvider.propTypes = {
  /**
   * The React children.
   */
  children: _propTypes.default.node
};